import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    BodyType,
    DriveType,
    FacilityTypeCategory,
    RegisterServiceType,
    RegisterUserType,
    ServiceUserStatus,
    ServiceUserType, VehicleFuelType,
    WorksheetStatus
} from '@services/backend/models/generated-models';
import { StorageService } from '@services/storage/storage.service';
import { Constants } from '@shared/constants';
import { SelectOption } from '@shared/select-option';
import { TranslateConfig } from '@shared/translate-config';
import { loadMessages, locale } from 'devextreme/localization';
import { IList, List } from 'linq-collections';
import { ReplaySubject } from 'rxjs';
import enMessages from '../../../assets/locale/ndru-devextreme-en.json';
import huMessages from '../../../assets/locale/ndru-devextreme-hu.json';
import { PlatformService } from '../platform/platform.service';
import { FacilityDurationUnit } from '@services/backend/models/custom-models';

@Injectable({ providedIn: 'root' })
export class LanguageService {

    // region Fields

    readonly languageInitialized: ReplaySubject<boolean>;

    registerUserTypes: IList<SelectOption>;
    registerServiceTypes: IList<SelectOption>;

    serviceUserTypes: IList<SelectOption>;
    serviceUserStatus: IList<SelectOption>;

    worksheetStatus: IList<SelectOption>;
    
    supplierStatus: IList<SelectOption>;
    partStatus: IList<SelectOption>;

    facilityTypeCategories: IList<SelectOption>;

    vehicleFuelTypes: IList<SelectOption>;
    vehicleBodyTypes: IList<SelectOption>;
    vehicleDriveTypes: IList<SelectOption>;

    facilityDurationUnits: IList<SelectOption>;

    language: string;

    supportedLanguages: Array<SelectOption>;

    // endregion

    // region Initialization

    constructor(
        private storage: StorageService,
        private translate: TranslateService,
        private platform: PlatformService,
    ) {
        this.languageInitialized = new ReplaySubject<boolean>(1);

        this.language = null;

        this.supportedLanguages = [{ caption: 'HU', value: 'hu-HU' }, { caption: 'EN', value: 'en-US' }];
    }

    /**
     * Runs at application start
     */
    async init() {
        // Get language from storage first
        let language: string = this.storage.getLanguage();
        if (language == null) {
            // Language should default to hu
            language = this.translate.getBrowserCultureLang();
            
            if (language != 'hu-HU' && language != 'en-US' && language != 'en-GB') {
                language = Constants.defaultLanguage;
            }
        }

        if (language == 'en-GB') {
            language = 'en-US';
        }

        // Load DevExtreme localization
        if (language == 'hu-HU') {
            loadMessages(huMessages);
        }
        else {
            loadMessages(enMessages);
        }

        await this.setLanguage(language);
    }

    async setLanguage(language: string) {
        if (this.language != language) {
            this.language = language;

            this.storage.setLanguage(this.language);

            await TranslateConfig.init(this.translate, this.language.substr(0, 2));

            // Set DevExtreme locale
            locale(this.language);

            this.initSelectOptions();

            this.languageInitialized.next(true);
        }
    }

    initSelectOptions() {
        // TODO add additional entities here
        // TODO also filter by permissions
        this.facilityDurationUnits = new List<SelectOption>([
            { value: FacilityDurationUnit.Days, caption: this.translate.instant('facility_duration_unit_day') },
            { value: FacilityDurationUnit.Hours, caption: this.translate.instant('facility_duration_unit_hour') },
            { value: FacilityDurationUnit.Minutes, caption: this.translate.instant('facility_duration_unit_minute') }
        ]);

        this.registerUserTypes = new List<SelectOption>([
            { value: RegisterUserType.ServiceManager, caption: this.translate.instant('register_user_type_service_manager') },
            { value: RegisterUserType.FleetManager, caption: this.translate.instant('register_user_type_fleet_manager') }
        ]);
        this.registerServiceTypes = new List<SelectOption>([
            { value: RegisterServiceType.Service, caption: this.translate.instant('register_service_type_service') },
            { value: RegisterServiceType.CarWash, caption: this.translate.instant('register_service_type_car_wash') },
            { value: RegisterServiceType.TireShop, caption: this.translate.instant('register_service_type_tireshop') },
            { value: RegisterServiceType.MOTTestingStation, caption: this.translate.instant('register_service_type_MOT_test_station') }
        ]);
        this.serviceUserTypes = new List<SelectOption>([
            { value: ServiceUserType.Manager, caption: this.translate.instant('user_management_type_manager') },
            { value: ServiceUserType.Mechanic, caption: this.translate.instant('user_management_type_mechanic') },
            { value: ServiceUserType.Warehouse, caption: this.translate.instant('user_management_type_warehouse') },
            { value: ServiceUserType.Delivery, caption: this.translate.instant('user_management_type_delivery') }
        ]);
        this.serviceUserStatus = new List<SelectOption>([
            { value: ServiceUserStatus.Active, caption: this.translate.instant('service_user_status_active') },
            { value: ServiceUserStatus.Inactive, caption: this.translate.instant('service_user_status_inactive') }
        ]);
        this.worksheetStatus = new List<SelectOption>([
            { value: WorksheetStatus.AppointmentReserved, caption: this.translate.instant('worksheet_status_appointment_reserved'), visible: true },
            { value: WorksheetStatus.AppointmentConfirmed, caption: this.translate.instant('worksheet_status_appointment_confirmed'), visible: true },
            { value: WorksheetStatus.NeedsPickUp, caption: this.translate.instant('worksheet_status_needs_pick_up'), visible: true },
            { value: WorksheetStatus.PickingUpVehicle, caption: this.translate.instant('worksheet_status_picking_up_vehicle'), visible: true },
            { value: WorksheetStatus.VehiclePickedUp, caption: this.translate.instant('worksheet_status_vehicle_picked_up'), visible: true },
            { value: WorksheetStatus.DeliveringVehicle, caption: this.translate.instant('worksheet_status_delivering_vehicle'), visible: true },
            { value: WorksheetStatus.VehicleArrived, caption: this.translate.instant('worksheet_status_vehicle_arrived'), visible: true },
            { value: WorksheetStatus.UnderReview, caption: this.translate.instant('worksheet_status_under_review'), visible: true },
            { value: WorksheetStatus.WaitingWarehousePricing, caption: this.translate.instant('worksheet_status_waiting_warehouse_pricing'), visible: true },
            { value: WorksheetStatus.WaitingManagerPricing, caption: this.translate.instant('worksheet_status_waiting_manager_pricing'), visible: true },
            { value: WorksheetStatus.WaitingConfirmation, caption: this.translate.instant('worksheet_status_waiting_confirmation'), visible: true },
            { value: WorksheetStatus.WaitingRepairParts, caption: this.translate.instant('worksheet_status_waiting_repair_parts'), visible: true },
            { value: WorksheetStatus.WaitingRepair, caption: this.translate.instant('worksheet_status_waiting_repair'), visible: true },
            { value: WorksheetStatus.UnderRepair, caption: this.translate.instant('worksheet_status_under_repair'), visible: true },
            { value: WorksheetStatus.WaitingCompletion, caption: this.translate.instant('worksheet_status_waiting_completion'), visible: true },
            { value: WorksheetStatus.WaitingPayment, caption: this.translate.instant('worksheet_status_waiting_payment'), visible: true },
            { value: WorksheetStatus.NeedsReturning, caption: this.translate.instant('worksheet_status_needs_returning'), visible: true },
            { value: WorksheetStatus.ReturningVehicle, caption: this.translate.instant('worksheet_status_returning_vehicle'), visible: true },
            { value: WorksheetStatus.VehicleReturned, caption: this.translate.instant('worksheet_status_vehicle_returned'), visible: true },
            { value: WorksheetStatus.Finished, caption: this.translate.instant('worksheet_status_finished'), visible: true },
            { value: WorksheetStatus.Cancelled, caption: this.translate.instant('worksheet_status_cancelled'), visible: true }
        ]);
        this.facilityTypeCategories = new List<SelectOption>([
            { value: FacilityTypeCategory.Repair, caption: this.translate.instant('facility_type_category_repair') },
            { value: FacilityTypeCategory.Hull, caption: this.translate.instant('facility_type_category_hull') },
            { value: FacilityTypeCategory.Washing, caption: this.translate.instant('facility_type_category_washing') },
            { value: FacilityTypeCategory.Management, caption: this.translate.instant('facility_type_category_management') }
        ]);
        
        this.vehicleFuelTypes = new List<SelectOption>([
            { value: VehicleFuelType.Gasoline, caption: this.translate.instant("vehicle_fuel_type_gasoline") },
            { value: VehicleFuelType.Diesel, caption: this.translate.instant("vehicle_fuel_type_diesel") },
            { value: VehicleFuelType.Hybrid, caption: this.translate.instant("vehicle_fuel_type_hybrid") },
            { value: VehicleFuelType.Electronic, caption: this.translate.instant("vehicle_fuel_type_electronic") },
            { value: VehicleFuelType.Gas, caption: this.translate.instant("vehicle_fuel_type_gas") },
            {
                value: VehicleFuelType.PetrolCNG,
                caption: this.translate.instant("vehicle_fuel_type_gasoline") + "/" + this.translate.instant("vehicle_fuel_type_cng"),
            },
            {
                value: VehicleFuelType.PetrolLPG,
                caption: this.translate.instant("vehicle_fuel_type_gasoline") + "/" + this.translate.instant("vehicle_fuel_type_lpg"),
            },
            {
                value: VehicleFuelType.PetrolEthanol,
                caption: this.translate.instant("vehicle_fuel_type_gasoline") + "/" + this.translate.instant("vehicle_fuel_type_ethanol"),
            },
            { value: VehicleFuelType.MildHybrid, caption: this.translate.instant("vehicle_fuel_type_hybrid_mild") },
            { value: VehicleFuelType.PlugInHybrid, caption: this.translate.instant("vehicle_fuel_type_hybrid_plugin") },
            { value: VehicleFuelType.FullHybrid, caption: this.translate.instant("vehicle_fuel_type_hybrid_full") },
        ]);

        this.vehicleBodyTypes = new List<SelectOption>([
            { value: BodyType.Coupe, caption: this.translate.instant("vehicle_body_type_coupe") },
            { value: BodyType.Hatch, caption: this.translate.instant("vehicle_body_type_hatch") },
            { value: BodyType.Estate, caption: this.translate.instant("vehicle_body_type_estate") },
            { value: BodyType.Saloon, caption: this.translate.instant("vehicle_body_type_saloon") },
            { value: BodyType.Convertible, caption: this.translate.instant("vehicle_body_type_convertible") },
            { value: BodyType.EstateVan, caption: this.translate.instant("vehicle_body_type_estate_van") },
            { value: BodyType.PanelVan, caption: this.translate.instant("vehicle_body_type_panel_van") },
            { value: BodyType.MPV, caption: this.translate.instant("vehicle_body_type_mpv") },
            { value: BodyType.VanBasedMPV, caption: this.translate.instant("vehicle_body_type_van_based_mpv") },
            { value: BodyType.Pickup, caption: this.translate.instant("vehicle_body_type_pickup") },
            { value: BodyType.OffRoadVehicle, caption: this.translate.instant("vehicle_body_type_off_road_vehicle") },
            { value: BodyType.ConvertibleOffRoadVehicle, caption: this.translate.instant("vehicle_body_type_convertible_off_road_vehicle") },
            { value: BodyType.SUV, caption: this.translate.instant("vehicle_body_type_suv") },
            { value: BodyType.ChassisCab, caption: this.translate.instant("vehicle_body_type_chassis_cab") },
            { value: BodyType.Van, caption: this.translate.instant("vehicle_body_type_van") },
            { value: BodyType.Bus, caption: this.translate.instant("vehicle_body_type_bus") },
            { value: BodyType.DumpTruck, caption: this.translate.instant("vehicle_body_type_dump_truck") },
            { value: BodyType.Hatchback, caption: this.translate.instant("vehicle_body_type_hatchback") },
            { value: BodyType.PlatformChassis, caption: this.translate.instant("vehicle_body_type_platform_chassis") },
            { value: BodyType.Targa, caption: this.translate.instant("vehicle_body_type_targa") },
            { value: BodyType.SUVOpen, caption: this.translate.instant("vehicle_body_type_suv_open") },
            { value: BodyType.Tipper, caption: this.translate.instant("vehicle_body_type_tipper") },
            { value: BodyType.Lorry, caption: this.translate.instant("vehicle_body_type_lorry") },
            { value: BodyType.SUVVan, caption: this.translate.instant("vehicle_body_type_suv_van") },
            { value: BodyType.VanMPV, caption: this.translate.instant("vehicle_body_type_van_mpv") },
            { value: BodyType.HatchbackVan, caption: this.translate.instant("vehicle_body_type_hatchback_van") },
            { value: BodyType.ClosedOffRoadVan, caption: this.translate.instant("vehicle_body_type_closed_off_road_van") },
        ]);

        this.vehicleDriveTypes = new List<SelectOption>([
            { value: DriveType.FrontWheelDrive, caption: this.translate.instant("vehicle_drive_type_fwd") },
            { value: DriveType.RearWheelDrive, caption: this.translate.instant("vehicle_drive_type_rwd") },
            { value: DriveType.AllWheelDrive, caption: this.translate.instant("vehicle_drive_type_awd") },
        ]);
    }

    // endregion

    // region Methods

    /*
     * Awaitable function to get translation
     */
    get(key: string): Promise<string> {
        return new Promise(resolve => {
            this.translate.get(key)
                .subscribe(
                    resolve
                );
        });
    }

    getEditorStylingMode(): string {
        // DevExtreme editor style options: 'underlined', 'filled', 'outlined'
        return this.platform.isMobile ? 'underlined' : 'outlined';
    }

    getTranslation(key: string): string {
        return this.translate.instant(key);
    }

    // endregion
}
